/**
 * Unified project data hook.
 *
 * Merges:
 *   - 2 hardcoded mock projects from src/data/mock.js (cannot be edited
 *     or deleted; can be hidden via KV `vis:<id>`)
 *   - N dynamic projects fetched from GET /api/projects (full CRUD via
 *     /api/admin/projects)
 *
 * Both shapes are converted to a common "DisplayProject" form, so
 * Projects.jsx and ProjectDetail.jsx don't need to know the source.
 *
 * DisplayProject shape:
 *   {
 *     id, source: 'mock' | 'kv',
 *     year, month?, tags, title{en,de}, summary{en,de}, thumbnail,
 *     // For mock projects we synthesise sections from the structured
 *     // fields (problem/objective/approach/...) — same visual order
 *     // as the existing ProjectDetail page.
 *     sections: [{ key{en,de}, value{en,de} }, ...],
 *     media: [{ url, type, caption{en,de} }, ...],
 *     hidden: boolean,
 *   }
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { projects as mockProjects } from '../data/mock';

const arrToText = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).join('\n\n') : '');
const biArrToText = (b) => ({ en: arrToText(b?.en), de: arrToText(b?.de) });

const SECTION_LABELS = {
  problem: { en: 'Problem statement', de: 'Problemstellung' },
  objective: { en: 'Objective', de: 'Zielsetzung' },
  approach: { en: 'Approach', de: 'Vorgehensweise' },
  tools: { en: 'Tools & methods', de: 'Werkzeuge & Methoden' },
  results: { en: 'Results', de: 'Ergebnisse' },
  learnings: { en: 'Learnings', de: 'Erkenntnisse' },
  conclusion: { en: 'Conclusion', de: 'Fazit' },
};

function mockToDisplay(p) {
  const sections = [];

  if (p.problem) sections.push({ key: SECTION_LABELS.problem, value: p.problem });
  if (p.objective) sections.push({ key: SECTION_LABELS.objective, value: p.objective });
  if (p.approach) sections.push({ key: SECTION_LABELS.approach, value: biArrToText(p.approach) });
  if (Array.isArray(p.tools) && p.tools.length) {
    sections.push({
      key: SECTION_LABELS.tools,
      value: { en: p.tools.join(' · '), de: p.tools.join(' · ') },
    });
  }
  if (p.results) sections.push({ key: SECTION_LABELS.results, value: biArrToText(p.results) });
  if (p.learnings) sections.push({ key: SECTION_LABELS.learnings, value: p.learnings });
  if (p.conclusion) sections.push({ key: SECTION_LABELS.conclusion, value: p.conclusion });

  return {
    id: p.id,
    source: 'mock',
    year: p.year,
    month: p.month || '',
    context: p.context || { en: 'Project', de: 'Projekt' },
    tools: Array.isArray(p.tools) ? p.tools : [],
    tags: Array.isArray(p.tags) ? p.tags : [],
    title: p.title || { en: '', de: '' },
    summary: p.summary || { en: '', de: '' },
    thumbnail: p.thumbnail,
    sections,
    media: Array.isArray(p.media)
      ? p.media.map((m) => ({
          src: m.src,
          url: m.src,
          type: m.type || 'image',
          caption: m.caption || { en: '', de: '' },
        }))
      : [],
    hidden: false,
    _raw: p,
  };
}

function kvToDisplay(p) {
  const ctx = Array.isArray(p.tags) && p.tags.length
    ? { en: p.tags[0], de: p.tags[0] }
    : { en: 'Project', de: 'Projekt' };
  return {
    id: p.id,
    source: 'kv',
    year: p.year,
    month: p.month || '',
    context: ctx,
    tools: Array.isArray(p.tags) ? p.tags : [],
    tags: Array.isArray(p.tags) ? p.tags : [],
    title: p.title || { en: '', de: '' },
    summary: p.summary || { en: '', de: '' },
    thumbnail: p.thumbnail,
    sections: Array.isArray(p.sections) ? p.sections : [],
    media: Array.isArray(p.media)
      ? p.media.map((m) => ({
          src: m.url,
          url: m.url,
          type: m.type || (m.url?.includes('/video/upload/') ? 'video' : 'image'),
          caption: m.caption || { en: '', de: '' },
        }))
      : [],
    hidden: !!p.hidden,
    _raw: p,
  };
}

const sortByYearDesc = (a, b) => {
  const ay = `${a.year || ''}-${a.month || '00'}`;
  const by = `${b.year || ''}-${b.month || '00'}`;
  return by.localeCompare(ay);
};

/**
 * Public hook (no admin auth required).
 */
export function useProjects() {
  const [dynamic, setDynamic] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/projects');
      const ct = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('application/json')) {
        throw new Error(`Projects API ${res.status}`);
      }
      const data = await res.json();
      setDynamic(Array.isArray(data.projects) ? data.projects : []);
      setHiddenIds(new Set(Array.isArray(data.hidden_ids) ? data.hidden_ids : []));
    } catch (e) {
      // Non-fatal: still show mock projects when API is unreachable
      setDynamic([]);
      setHiddenIds(new Set());
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const projects = useMemo(() => {
    const merged = [
      ...mockProjects.filter((p) => !hiddenIds.has(p.id)).map(mockToDisplay),
      ...dynamic.map(kvToDisplay),
    ];
    return merged.sort(sortByYearDesc);
  }, [dynamic, hiddenIds]);

  return { projects, loading, error, refresh };
}

/**
 * Admin hook — also returns hidden mock projects + visibility map so the
 * admin UI can show toggles. Requires the user to be authenticated by
 * Cloudflare Access; the underlying GET /api/admin/projects 401s otherwise.
 */
export function useAdminProjects() {
  const [dynamic, setDynamic] = useState([]);
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/projects', { credentials: 'same-origin' });
      const ct = res.headers.get('content-type') || '';
      if (res.status === 401 || res.status === 403 || !ct.includes('application/json')) {
        setError('unauthorized');
        return;
      }
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to load');
      setDynamic(Array.isArray(data.projects) ? data.projects : []);
      setVisibility(data.visibility || {});
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const allProjects = useMemo(() => {
    return [
      ...mockProjects.map((p) => ({ ...mockToDisplay(p), hidden: !!visibility[p.id] })),
      ...dynamic.map((p) => ({ ...kvToDisplay(p), hidden: p.hidden || !!visibility[p.id] })),
    ].sort(sortByYearDesc);
  }, [dynamic, visibility]);

  return { allProjects, dynamic, visibility, loading, error, refresh };
}

export { mockToDisplay, kvToDisplay };
